import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';

import * as fromAppointment from './appointment.reducer';
import * as AppointmentActions from './appointment.actions';
import * as AppointmentSelectors from './appointment.selectors';
import { APIService } from '../../services/api/api-service';
import { AppState } from '../app.states';

@Injectable()
export class AppointmentEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private notification: NzNotificationService,
    private api: APIService
  ) {}

  loadAllAppointments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentActions.loadAllAppointments),
      switchMap(action =>
        this.api.loadAllAppointments().pipe(
          map(appointments => {
            return AppointmentActions.loadAllAppointmentsSuccess({
              appointments
            });
          }),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Appointments',
              error?.error?.message || 'Failed to load'
            );
            return of(
              AppointmentActions.loadAllAppointmentsFailure({ error })
            );
          })
        )
      )
    )
  );

  loadAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentActions.loadAppointment),
      switchMap(action =>
        this.api.loadAppointment(action.id).pipe(
          map(appointment => {
            return AppointmentActions.loadAppointmentSuccess({
              appointment
            });
          }),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Appointment',
              error?.error?.message || 'Failed to load'
            );
            return of(AppointmentActions.loadAppointmentFailure({ error }));
          })
        )
      )
    )
  );

  loadAppointmentSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentActions.loadAppointmentSuccess),
      withLatestFrom(this.store.select(AppointmentSelectors.getPosition)),
      switchMap(([action, position]) => {
        if (position === null) {
          return of(AppointmentActions.getQueuePosition());
        }
        return [];
      })
    )
  );

  getQueuePosition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentActions.getQueuePosition),
      withLatestFrom(this.store.select(AppointmentSelectors.getSelectedId)),
      switchMap(([action, appointmentId]) =>
        this.api.getQueuePosition(appointmentId).pipe(
          map(position => {
            return AppointmentActions.getQueuePositionSuccess({
              position
            });
          }),
          catchError(error => {
            console.error('Error', error);
            return of(AppointmentActions.getQueuePositionFailure({ error }));
          })
        )
      )
    )
  );

  getQueuePositionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentActions.getQueuePositionSuccess),
      withLatestFrom(this.store.select(AppointmentSelectors.getSelectedId)),
      switchMap(([action, appointmentId]) => {
        if (action.position < 0) {
          return of(AppointmentActions.loadAppointment({ id: appointmentId }));
        }
        return [];
      })
    )
  );

  updateAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentActions.updateAppointment),
      switchMap(action =>
        this.api.updateAppointment(action.id, action.appointment).pipe(
          tap(() => this.notification.success('Successfully updated.', '')),
          map(appointment =>
            AppointmentActions.updateAppointmentSuccess({ appointment })
          ),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error updating appointment.'
            );
            return of(AppointmentActions.updateAppointmentFailure({ error }));
          })
        )
      )
    )
  );
}
